import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import {
    selectGuardianInfo,
    selectDuplicatedEmail,
    selectLegalUpdating
} from '../selectors/legal.selector';
import { updateGuardianInfo } from '../actions/legal.actions';
import { GuardianInfo } from '../types/legal.types';
import { checkEmailExisted } from '../actions/tax.actions';
import { selectTaxProfileState } from '../selectors/tax.selector';
import { clearError, resetDuplicatedEmail } from '../reducer/legal.reducer';
import { createGuardianValidationSchema } from '../utils/guardianValidation';
import { selectAccountEmail } from '../selectors/account.selector';

interface UseGuardianInfoFormProps {
    guardianEmail?: string;
}

export const useGuardianInfoForm = ({ guardianEmail }: UseGuardianInfoFormProps = {}) => {
    const dispatch = useDispatch();
    const guardianInfo = useSelector(selectGuardianInfo);
    const accountEmail = useSelector(selectAccountEmail);
    const updating = useSelector(selectLegalUpdating);
    const taxProfileState = useSelector(selectTaxProfileState);
    const duplicatedEmail = useSelector(selectDuplicatedEmail);
    const { t } = useTranslation();

    useEffect(() => {
        if (guardianEmail) {
            dispatch(checkEmailExisted(guardianEmail) as any);
        }
        return () => {
            dispatch(clearError());
        };
    }, [guardianEmail, dispatch]);

    const validationSchema = createGuardianValidationSchema({
        t,
        guardianEmail,
        accountEmail,
        emailExisted: taxProfileState?.emailExisted
    });

    const formik = useFormik({
        initialValues: {
            guardianFirstName: guardianInfo?.guardianFirstName || '',
            guardianLastName: guardianInfo?.guardianLastName || '',
            guardianPhoneNumber: guardianInfo?.guardianPhoneNumber || '',
            guardianEmail: taxProfileState?.emailExisted
                ? ''
                : guardianEmail || guardianInfo?.guardianEmail || ''
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            const guardianData: GuardianInfo = {
                guardianFirstName: values.guardianFirstName,
                guardianLastName: values.guardianLastName,
                guardianPhoneNumber: values.guardianPhoneNumber,
                guardianEmail: values.guardianEmail
            };
            // @ts-ignore
            await dispatch(updateGuardianInfo(guardianData));
        }
    });

    const handleChangeFormValues = useCallback(
        (_: string, evt: any) => {
            if (evt?.target?.name === 'guardianEmail' && duplicatedEmail) {
                dispatch(resetDuplicatedEmail());
            }
            formik.handleChange(evt);
            formik.setFieldTouched(evt.target.name, true, false);
        },
        [formik]
    );

    return {
        formik,
        handleChangeFormValues,
        updating,
        emailExisted: taxProfileState?.emailExisted,
        checkingEmail: taxProfileState?.checkingEmail,
        duplicatedEmail
    };
};
